
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { identifyUser, resetUser, trackEvent } from '@/lib/mixpanel';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userCountry: string | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;

  // signUp: (email: string, password: string, metadata?: { firstName?: string; lastName?: string; country?: string }) => Promise<{ error: any }>;
  signUp: (email: string, password: string, metadata?: { firstName?: string; lastName?: string; country?: string }, redirectPath?: string) => Promise<{ error: any }>;
  // signInWithGoogle: () => Promise<{ error: any }>;
  signInWithGoogle: (redirectPath?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userCountry, setUserCountry] = useState<string | null>(null);

  // Helper function to fetch user country from database
  const fetchUserCountry = async (userId: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('country')
        .eq('auth_user_id', userId)
        .maybeSingle();

      if (!error && data?.country) {
        return data.country;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user country:', error);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Track auth events in Mixpanel
        if (session?.user) {
          identifyUser(session.user.id, {
            email: session.user.email,
            created_at: session.user.created_at,
          });
          if (event === 'SIGNED_IN') {
            trackEvent('User Signed In');
          }

          // Fetch country in the background (don't block loading on it)
          fetchUserCountry(session.user.id).then((country) => {
            setUserCountry(country);
          });
        } else {
          setUserCountry(null);
          if (event === 'SIGNED_OUT') {
            resetUser();
          }
        }

        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Identify existing user
      if (session?.user) {
        identifyUser(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });

        // Fetch country in the background (don't block loading on it)
        fetchUserCountry(session.user.id).then((country) => {
          setUserCountry(country);
        });
      } else {
        setUserCountry(null);
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (!error) {
      trackEvent('Sign In Attempted', { success: true });
    } else {
      trackEvent('Sign In Attempted', { success: false, error: error.message });
    }
    return { error };
  };

    // const signUp = async (email: string, password: string, metadata?: { firstName?: string; lastName?: string; country?: string }) => {
    // const redirectUrl = `${window.location.origin}/`;
  const signUp = async (email: string, password: string, metadata?: { firstName?: string; lastName?: string; country?: string }, redirectPath?: string) => {
    const redirectUrl = `${window.location.origin}${redirectPath || '/'}`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata
          ? {
            first_name: metadata.firstName,
            last_name: metadata.lastName,
            country: metadata.country ?? null,
          }
          : undefined,
      }
    });

    // Supabase returns a user with empty identities when a confirmed user already exists
    // instead of throwing an explicit error. Detect this and surface a clear error.
    const isExistingUser =
      !error &&
      data?.user &&
      Array.isArray(data.user.identities) &&
      data.user.identities.length === 0;

    if (isExistingUser) {
      const duplicateError = { message: 'User already registered' };
      trackEvent('Sign Up Attempted', { success: false, error: duplicateError.message });
      return { error: duplicateError };
    }

    if (!error) {
      trackEvent('Sign Up Attempted', { success: true });
    } else {
      trackEvent('Sign Up Attempted', { success: false, error: error.message });
    }
    return { error };
  };
  

  // const signInWithGoogle = async () => {
  // const redirectUrl = `${window.location.origin}/`;

  const signInWithGoogle = async (redirectPath?: string) => {
    const redirectUrl = `${window.location.origin}${redirectPath || '/'}`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      },
    });
    if (!error) {
      trackEvent('Google Sign In Attempted', { success: true });
    } else {
      trackEvent('Google Sign In Attempted', { success: false, error: error.message });
    }
    return { error };
  };

  const signOut = async () => {
    trackEvent('User Signed Out');
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    if (!error) {
      trackEvent('Password Reset Requested', { success: true });
    } else {
      trackEvent('Password Reset Requested', { success: false, error: error.message });
    }
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (!error) {
      trackEvent('Password Updated', { success: true });
    } else {
      trackEvent('Password Updated', { success: false, error: error.message });
    }
    return { error };
  };



  const value = {
    user,
    session,
    loading,
    userCountry,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
